import React, { useEffect, useState } from "react";
import { BankData } from "../../../types";
import { GroupButton } from "../../common";
import EditIcon from "../../../assets/icons/EditIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import { EditBankAccount } from "./edit";
import { BankAccountDeletePrompt } from "./delete";
import PageLoader from "../../common/PageLoader";
import InfoIcon from "../../../assets/icons/InfoIcon";
import ArrowLeft from "../../../assets/icons/ArrowLeft";
import ArrowRight from "../../../assets/icons/ArrowRight";
import SuccessBanner from "../../common/Banner/SuccessBanner";
import { useTranslation } from "react-i18next";
import { Loading } from "../../common/Table/Loading";
import LoadingSpinner from "../../common/loadingSpinner";

interface ViewProps {
  bankAccount: BankData;
  onToggleActive: () => Promise<boolean>;
  onTogglePayout: () => Promise<boolean>;
  onEdit: () => void;
  accountIndex: number;
}

export const BankAccountView: React.FC<ViewProps> = ({
  bankAccount,
  onToggleActive,
  accountIndex,
  onEdit,
  onTogglePayout,
}) => {
  const { t } = useTranslation("settings");
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPayoutLoading, setIsPayoutLoading] = useState(false);
  const [isActive, setIsActive] = useState(bankAccount.isActive);
  const [isPayoutAccount, setIsPayoutAccount] = useState(
    bankAccount.isPayoutAccount,
  );
  const [bankAccountState, setBankAccountState] =
    useState<BankData>(bankAccount);
  const [isHovered, setIsHovered] = useState(false);
  const [isHovered2, setIsHovered2] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  useEffect(() => {
    setIsActive(bankAccount.isActive);
    setIsPayoutAccount(bankAccount.isPayoutAccount);
    setBankAccountState(bankAccount);
  }, [bankAccount]);

  useEffect(() => {
    if (deleteSuccess) {
      const timer = setTimeout(() => {
        setDeleteSuccess(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [deleteSuccess]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  const handleMouseEnter2 = () => {
    setIsHovered2(true);
  };

  const handleMouseLeave2 = () => {
    setIsHovered2(false);
  };

  const handleOpenDeletePrompt = () => {
    setShowDeletePrompt(true);
  };
  const handleCloseDeletePrompt = () => {
    setShowDeletePrompt(false);
  };

  const handleToggleActive = async () => {
    setIsLoading(true);
    const data = await onToggleActive();
    if (data) {
      setIsActive(!isActive);
    }
    setIsLoading(false);
  };

  const handleTogglePayout = async () => {
    setIsPayoutLoading(true);
    const data = await onTogglePayout();
    if (data) {
      setIsPayoutAccount(!isPayoutAccount);
    }
    setIsPayoutLoading(false);
  };

  return (
    <>
      <div className="w-full  border border-[#EAECF0] rounded-lg text-[#000000]">
        <div className="flex justify-between items-start md:p-5 p-[14px]">
          <div className="">
            <p className="font-semibold md:text-[16px] text-[14px] leading-5 mb-2 mt-2">
              {bankAccountState.bankName}
            </p>
            <p className="font-medium text-[#344054] md:text-[16px] text-[14px] leading-5 mt-[6px]">
              {bankAccountState.accountNumber}
            </p>{" "}
            <span className="font-medium text-[#344054] md:text-[16px] leading-5 mt-[8px] text-[14px]">
              {bankAccountState.accountName}
            </span>
          </div>
          <div className="text-[#044E97] text-[20px] md:text-[17px] lg:text-[14+px] font-medium ">
            <GroupButton
              styles={false}
              buttons={[
                {
                  text: "",
                  icon: <DeleteIcon />,
                  onClick: handleOpenDeletePrompt,
                  fontSize: "!px-2",
                },
              ]}
            />
          </div>
        </div>
        <hr className="border-[#EAECF0]" />

        <div className="md:px-5 md:py-3 px-[14px] py-[10px] flex 320:text-[12px] 360:text-[13px] 390:text-[14px] md:text-[16px] items-center justify-between">
          <div className="flex gap-2 items-center text-[#344054] font-medium">
            <span>{`${
              isActive
                ? "Active Bank Account"
                : `${t("Settings.inactivebankaccount")}`
            }`}</span>
            <div
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <InfoIcon color="#404040" />
              {isHovered && (
                <div className="absolute w-[204px] left-[-5.7em]">
                  <div className="flex items-center justify-center">
                    <svg
                      width="18"
                      height="15"
                      viewBox="0 0 18 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M9 0L17.6603 15H0.339746L9 0Z" fill="#FFF4C6" />
                    </svg>
                  </div>
                  <div className="p-3 bg-[#FFF4C6] rounded-[4px] text-[12px] font-medium leading-[14.4px]">
                    Inactive bank accounts will not reflect in your invoice.
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>{isLoading && <LoadingSpinner />}</div>

          <div
            className={`w-16 h-8 p-1 rounded-[69.33px] border-[0.53px] border-gray-300 flex items-center transition duration-200 ease-in ${
              isActive
                ? "bg-[#044E97] justify-end"
                : "bg-[#ffffff] justify-start"
            }`}
          >
            <div
              onClick={handleToggleActive}
              className={`${
                isActive ? "bg-[#ffffff]" : "bg-gray-300"
              } cursor-pointer w-6 h-6 rounded-full flex justify-center items-center`}
            ></div>
          </div>
        </div>

        <hr className="border-[#EAECF0]" />

        <div className="md:px-5 md:py-3  px-[14px] py-[10px] flex 320:text-[12px] 360:text-[13px] 390:text-[14px] md:text-[16px] items-center justify-between">
          <div className="flex gap-2 items-center text-[#344054] font-medium">
            <span>{`${
              isPayoutAccount
                ? "Payout Account"
                : "Use this account for Paystack verification"
            }`}</span>
            <div
              className="relative"
              onMouseEnter={handleMouseEnter2}
              onMouseLeave={handleMouseLeave2}
            >
              <InfoIcon color="#404040" />
              {isHovered2 && (
                <div className="absolute w-[204px] left-[-5.7em]">
                  <div className="flex items-center justify-center">
                    <svg
                      width="18"
                      height="15"
                      viewBox="0 0 18 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M9 0L17.6603 15H0.339746L9 0Z" fill="#FFF4C6" />
                    </svg>
                  </div>
                  <div className="p-3 bg-[#FFF4C6] rounded-[4px] text-[12px] font-medium leading-[14.4px]">
                    All payment from your customers will be sent to this
                    account.
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>{isPayoutLoading && <LoadingSpinner />}</div>

          <div
            className={`w-16 h-8 p-1 rounded-[69.33px] border-[0.53px] border-gray-300 flex items-center transition duration-200 ease-in ${
              isPayoutAccount
                ? "bg-[#044E97] justify-end"
                : "bg-[#ffffff] justify-start"
            }`}
          >
            <div
              onClick={handleTogglePayout}
              className={`${
                isPayoutAccount ? "bg-[#ffffff]" : "bg-gray-300"
              } cursor-pointer w-6 h-6 rounded-full flex justify-center items-center`}
            ></div>
          </div>
        </div>
      </div>
      {deleteSuccess && (
        <SuccessBanner message={"Account deleted successfully"} />
      )}

      <BankAccountDeletePrompt
        setDeleteSuccess={setDeleteSuccess}
        bankName={bankAccountState.bankName}
        accountNumber={bankAccountState.accountNumber}
        accountName={bankAccountState.accountName}
        showPrompt={showDeletePrompt}
        bankAccountId={accountIndex}
        onClose={handleCloseDeletePrompt}
      />
    </>
  );
};
